import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Sequencer, Synthetizer } from 'spessasynth_lib';

import { MIDI } from 'spessasynth_core';

import { getCountInPlan, getMidiBpm, safeFlushMidi, setMidiBpm } from './midiTempo';

const MusicEngineContext = createContext(null);

export const useMusicEngineInternal = () => {
  const ctx = useContext(MusicEngineContext);
  if (!ctx) {
    throw new Error('useMusicEngine must be used within MusicEngineProvider');
  }
  return ctx;
};

function formatTime(totalSeconds) {
  totalSeconds = Math.floor(totalSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds - minutes * 60);
  const time = `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
  return { minutes, seconds, time };
}

export const MusicEngineProvider = ({
  children,
  soundFontUrl = '../GeneralUserGS.sf3',
}) => {
  const [statusMsg, setStatusMsg] = useState(
    'Please wait while soundfonts are loading ...'
  );
  const initPromiseRef = useRef(null);
  const audioContextRef = useRef(null);
  const synthRef = useRef(null);
  const soundFontRef = useRef(null);
  const midiMediaRef = useRef(new Map()); 
  // key: playerId, value: { path, filename }


  // Global sequencer
  const sequencerRef = useRef(null);
  const midiRef = useRef(new Map());
  const originalMidiBufferRef = useRef(new Map()); // <-- ren originalkälla per player

  const [activePlayerId, setActivePlayerId] = useState(null);
  // Vilken spelare mellanslag ska styra - satt av internalPlay (play-knappen,
  // "spela från början") och seekByProgress (progress-controllern), skild från
  // activePlayerId eftersom den ska bestå även när spelaren pausas (annars går
  // det inte att återuppta med mellanslag).
  const [keyboardPlayerId, setKeyboardPlayerId] = useState(null);
  const [players, setPlayers] = useState({});
  const [mixer, setMixer] = useState({
    mainVolume: 50,
    mainPan: 0,
    pianoVolume: 100,
    bassVolume: 100,
    drumsVolume: 100,
    melodyVolume: 100,
    // Volymen på inräkningsklicken - global för alla spelare (till skillnad
    // från t.ex. loopEnabled/countInEnabled som är per spelare), precis som
    // övriga mixer-inställningar.
    countInVolume: 70,
  });

  const progressIntervalRef = useRef(null);
  const activePlayerIdRef = useRef(null);
  const keyboardPlayerIdRef = useRef(null);
  const lastPlayerIdRef = useRef(null);
  const lastLoadedPlayerIdRef = useRef(null);
  const playersRef = useRef({});
  // playerId -> { timeoutId, oscillators: [] } för pågående inräkning, så den
  // kan avbrytas (och de redan schemalagda klick-ljuden tystas) om spelaren
  // pausas/stängs innan inräkningen hunnit spela klart.
  const countInRef = useRef(new Map());

/*   useEffect(() => {
    ensureSynth();
  }, []); */

  useEffect(() => {
    playersRef.current = players;
  }, [players]);

  useEffect(() => {
    keyboardPlayerIdRef.current = keyboardPlayerId;
  }, [keyboardPlayerId]);

  // ------------------------------------------------------
  // Init SoundFont + AudioContext + Synth
  // ------------------------------------------------------
  const ensureSynth = async () => {
    if (initPromiseRef.current) {
      return initPromiseRef.current;
    }

    if (synthRef.current && audioContextRef.current && soundFontRef.current) {
      return Promise.resolve();
    }

    initPromiseRef.current = (async () => {
      if (!soundFontRef.current) {
        const response = await fetch(soundFontUrl);
        const buf = await response.arrayBuffer();
        soundFontRef.current = buf;
        setStatusMsg('SoundFont has been loaded!');
      }

      if (!audioContextRef.current) {
        const context = new AudioContext();
        await context.audioWorklet.addModule('../worklet_processor.min.js');
        audioContextRef.current = context;
      }

      if (!synthRef.current) {
        const synth = new Synthetizer(
          audioContextRef.current.destination,
          soundFontRef.current
        );
        synthRef.current = synth;
      }
    })();

    await initPromiseRef.current;
    initPromiseRef.current = null;
  };

  // spessasynth's seek logic (_playTo) fast-forwards through every event up to
  // the target time to sync channel state (program/CC/pitch bend), but note-on
  // events in that range are deliberately swallowed rather than sounded - they're
  // assumed to be "in the past" relative to the seek target. Since normalizeMidi()
  // shifts every song so its first note lands on tick 0, nudging the seek target
  // to a hair past 0 (as a previous version of this code did, to work around a
  // separate bug where an initial ProgramChange at tick 0 was never applied) means
  // that pass fast-forwards straight through the first note too, killing it
  // silently. Seeking to exactly 0 makes _playTo break out before consuming
  // anything, which is what we want at the very start of a song - any tick-0
  // state (including that ProgramChange) still gets applied for real once
  // playback actually starts ticking forward from 0.
  const SEEK_EPSILON = 0.0001;
  const seekSequencerTo = (seq, time) => {
    let target = Math.max(0, time);
    // spessasynth's play() treats "currentTime >= duration" as "already at the
    // end" and immediately jumps back to tick 0 on its own - seeking to exactly
    // (or past) the end via the progress slider would otherwise silently restart
    // the song from the beginning instead of landing on/near the last position.
    if (seq.duration > 0 && target >= seq.duration) {
      target = Math.max(0, seq.duration - SEEK_EPSILON);
    }
    seq.currentTime = target;
  };

  // Global sequencer
  const ensureSequencer = () => {
    const synth = synthRef.current;
    if (!synth) {
      return null;
    }
    if (!sequencerRef.current) {
      const seq = new Sequencer([], synth, {
        skipToFirstNoteOn: true,
      });
      seq.loop = false;
      sequencerRef.current = seq;
    }
    return sequencerRef.current;
  };

  const loadSongIntoSequencer = playerId => {
    const midi = midiRef.current.get(playerId);
    if (!midi) {
      return null;
    }
    const seq = ensureSequencer();
    if (!seq) return null;

    //console.log('loadSongIntoSequencer for', playerId, 'midi=', midi);

    const outputBuffer = midi.writeMIDI();
    seq.loadNewSongList([{ binary: outputBuffer }], false);

    lastLoadedPlayerIdRef.current = playerId;
    return seq;
  };

  // ------------------------------------------------------
  // Hjälpare: uppdatera players-state
  // ------------------------------------------------------
  function checksum(buf) {
    const view = new Uint8Array(buf);
    let sum = 0;
    for (let i = 0; i < Math.min(view.length, 500); i++) {
      sum = (sum + view[i]) & 0xffff;
    }
    return sum;
  }


  const updatePlayerState = (playerId, partial) => {
    setPlayers(prev => {
      const existing = prev[playerId] || {
        progress: 0,
        currentTime: 0,
        duration: 0,
        timeLabel: '--:--/--:--',
        hasSeq: false,
        transpose: 0,
        playbackRate: 1,
        bpmOverride: null,
        loopEnabled: false,
        countInEnabled: true,
        countingIn: false,
        cursorEnabled: true,
      };
      const next = { ...existing, ...partial };
      return { ...prev, [playerId]: next };
    });
  };

  // ------------------------------------------------------
  // MIDI helpers
  // ------------------------------------------------------
  const normalizeMidi = parsedMIDI => {
    const firstNoteTicks = parsedMIDI.firstNoteOn;
    parsedMIDI.tracks.forEach(track =>
      track.forEach(ev => {
        ev.ticks = Math.max(0, ev.ticks - firstNoteTicks);
      })
    );
    safeFlushMidi(parsedMIDI);
  };

  const transposeMidi = (parsedMIDI, semitones) => {
    if (!semitones) return;
    parsedMIDI.tracks.forEach(track => {
      track.forEach(ev => {
        if (!ev.messageData || ev.messageData.length === 0) return;
        const status = ev.messageStatusByte;
        const eventType = status & 0xF0;  // upper nibble: type
        const channel = status & 0x0F;    // lower nibble: channel (0-15)

        // hoppa över trummor på kanal 9 (10 i 1-baserad)
        if (channel === 9) return;

        if (eventType === 0x90 || eventType === 0x80) {
          const note = ev.messageData[0];
          const transposedNote = Math.max(0, Math.min(127, note + semitones));
          ev.messageData[0] = transposedNote;
        }
      });
    });
    if (typeof parsedMIDI.flush === 'function') {
      safeFlushMidi(parsedMIDI);
    }
  };

  // Bygger om den processade MIDI:n för en spelare från originalbufferten och
  // applicerar normalisering samt ev. transpose och BPM-override. Delas av
  // setTranspose/setBpmOverride (och initialladdningen) så de utgår från
  // samma opåverkade originalbuffert och inte råkar skriva över varandras
  // justeringar.
  const rebuildProcessedMidi = (playerId, overrides = {}) => {
    const originalBuf = originalMidiBufferRef.current.get(playerId);
    if (!originalBuf) return null;

    const saved = playersRef.current[playerId];
    const semitones = 'transpose' in overrides ? overrides.transpose : (saved?.transpose || 0);
    const bpmOverride = 'bpmOverride' in overrides ? overrides.bpmOverride : saved?.bpmOverride;

    const newMidi = new MIDI(originalBuf.slice(0));
    normalizeMidi(newMidi);
    if (semitones) {
      transposeMidi(newMidi, semitones);
    }
    if (bpmOverride) {
      setMidiBpm(newMidi, bpmOverride);
    }
    return newMidi;
  };

  // Sparar en ombyggd MIDI som spelarens aktiva version, uppdaterar state och
  // laddar om sequencern om spelaren råkar vara aktiv just nu.
  const commitRebuiltMidi = (playerId, newMidi) => {
    midiRef.current.set(playerId, newMidi);

    // 🔴 Viktigt: tvinga sequencern att ladda om vid nästa play
    lastLoadedPlayerIdRef.current = null;

    const duration = newMidi.duration || 0;
    const total = formatTime(duration);

    const prev = playersRef.current[playerId];
    const prevTime = prev?.currentTime || 0;

    updatePlayerState(playerId, {
      duration,
      currentTime: prevTime,
      progress: duration > 0 ? prevTime / duration : 0,
      timeLabel: `${formatTime(prevTime).time}/${total.time}`,
    });

    if (activePlayerIdRef.current !== playerId) return;

    const seq = ensureSequencer();
    if (!seq) return;

    const outputBuffer = newMidi.writeMIDI();
    seq.loadNewSongList([{ binary: outputBuffer }], false);
    seekSequencerTo(seq, prevTime);
    startProgressLoop();
  };

  // ------------------------------------------------------
  // Ladda MIDI (utan transpose)
  // ------------------------------------------------------
  const loadMidiFromArrayBuffer = async (playerId, buf) => {
    //await ensureSynth();
    //console.log('loadMidiFromArrayBuffer', playerId, 'buf size=', buf.byteLength);

    // 1. Spara ren originalbuffert
    const originalCopy = buf.slice(0);
    originalMidiBufferRef.current.set(playerId, originalCopy);

    // 2. Skapa MIDI för uppspelning
    const parsedMIDI = new MIDI(originalCopy);
    normalizeMidi(parsedMIDI);

    // 3. Spara normaliserad MIDI som aktiv version
    midiRef.current.set(playerId, parsedMIDI);

    // 4. Uppdatera state
    const duration = parsedMIDI.duration || 0;
    const total = formatTime(duration);

    updatePlayerState(playerId, {
      duration,
      currentTime: 0,
      progress: 0,
      timeLabel: `00:00/${total.time}`,
      hasSeq: true,
      // Inräkning och cursor-highlight ska alltid vara aktiverade som default
      // på spelaren - annars "ärver" en nyladdad låt en tidigare avstängd
      // inställning från den förra låten som spelats i samma spelare
      // (updatePlayerState slår bara ihop med befintligt state, den
      // nollställer inget som inte anges här).
      countInEnabled: true,
      cursorEnabled: true,
    });

    // 5. Om transpose eller BPM-override redan fanns i state → applicera dem nu
    const saved = playersRef.current[playerId];
    if ((saved?.transpose || 0) !== 0 || saved?.bpmOverride) {
      const rebuilt = rebuildProcessedMidi(playerId);
      if (rebuilt) commitRebuiltMidi(playerId, rebuilt);
    }
  };


  const loadMidiFromUrl = async (playerId, url) => {
    // url är t.ex. "/64ae8668708bbcb335546ac5/BlueBossa.mid"

    // Plocka ut path + filnamn
    let path = "/";
    let filename = null;
    if (typeof url === "string") {
      const parts = url.split("/");
      filename = parts.pop();              // "BlueBossa.mid"
      path = parts.join("/") || "/";       // "/64ae8668708bbcb335546ac5"
    }

    midiMediaRef.current.set(playerId, { path, filename });

    const fullUrl = `${process.env.REACT_APP_API_URL}/media${url}`;
    const response = await fetch(fullUrl);

    if (!response.ok) {
      updatePlayerState(playerId, {
        progress: 0,
        currentTime: 0,
        duration: 0,
        timeLabel: '--:--/--:--',
        hasSeq: false,
        transpose: 0,
        playbackRate: 1,
        bpmOverride: null,
        loopEnabled: false,
        countInEnabled: true,
        countingIn: false,
        cursorEnabled: true,
      });
      return;
    }

    const buf = await response.arrayBuffer();
    await loadMidiFromArrayBuffer(playerId, buf);
    // Markera att sequencern måste laddas om nästa gång spelaren startas
    lastLoadedPlayerIdRef.current = null;
  };

  const saveParsedMidi = async (playerId) => {
    const midi = getParsedMidi(playerId);
    if (!midi) throw new Error("No parsed MIDI to save");

    const mediaInfo = midiMediaRef.current.get(playerId);
    if (!mediaInfo) throw new Error("No media info for this player");

    const { path, filename } = mediaInfo;

    return await updateMidiOnServer(path, filename, midi.writeMIDI());
  };

  async function updateMidiOnServer(path, filename, midiBytes) {
    const blob = new Blob([midiBytes], { type: "audio/midi" });
    const file = new File([blob], filename, { type: "audio/midi" });

    const form = new FormData();
    form.append("file", file);
    form.append("path", path);

    const res = await fetch(`${process.env.REACT_APP_API_URL}/media`, {
      method: "PUT",
      body: form,
    });

    if (!res.ok) throw new Error("Failed to update MIDI");

    return await res.json();
  }

  // ------------------------------------------------------
  // Transpose – alltid från originalbufferten
  // ------------------------------------------------------
  const setTranspose = (playerId, semitones) => {
    updatePlayerState(playerId, { transpose: semitones });

    // låten kanske inte är laddad än – då räcker det att vi sparar transpose i state
    const newMidi = rebuildProcessedMidi(playerId, { transpose: semitones });
    if (!newMidi) return;

    commitRebuiltMidi(playerId, newMidi);
  };

  // BPM-override för en enskild spelare: om satt (icke-null/0) används det
  // istället för tempot i midifilen, annars gäller filens eget tempo. Sparas
  // bara i players[playerId] - till skillnad från mixerns inställningar är
  // det inte globalt state och delas inte mellan spelare.
  const setBpmOverride = (playerId, bpm) => {
    // bpm-propen kan komma in som sträng (t.ex. från en CMS-post eller URL),
    // så tolka den numeriskt istället för att kräva typeof === 'number'. Men
    // booleaner måste filtreras bort innan dess - Number(true) === 1, så en
    // stökig bpm={false}-default hade annars kunnat tolkas som "1 BPM" här.
    const numeric = typeof bpm === 'boolean' ? NaN : Number(bpm);
    const value = Number.isFinite(numeric) && numeric > 0 ? numeric : null;
    updatePlayerState(playerId, { bpmOverride: value });

    const newMidi = rebuildProcessedMidi(playerId, { bpmOverride: value });
    if (!newMidi) return;

    commitRebuiltMidi(playerId, newMidi);
  };

  // ------------------------------------------------------
  // Progress-loop
  // ------------------------------------------------------
  const stopProgressLoop = () => {
    if (progressIntervalRef.current !== null) {
      window.clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const startProgressLoop = () => {
    if (progressIntervalRef.current !== null) return;

    progressIntervalRef.current = window.setInterval(() => {
      const id = activePlayerIdRef.current;
      if (!id) return;

      const seq = sequencerRef.current;
      if (!seq) return;

      const playerState = playersRef.current[id];
      const duration = playerState?.duration || seq.duration || 0;

      const currentTime = seq.currentTime || 0;
      const ratio = duration > 0 ? currentTime / duration : 0;

      const time = formatTime(currentTime);
      const total = formatTime(duration);
      const timeLabel = `${time.time}/${total.time}`;

      updatePlayerState(id, {
        duration,
        currentTime,
        progress: ratio,
        timeLabel,
      });

      // Normally the song reaching its end is caught by ratio >= 1, but that's a
      // race between our own lastVoiceEventTick-based duration and whichever
      // track the engine itself runs out of events on first - especially right
      // after seeking, which can leave currentTime frozen just a hair under our
      // duration. seq.paused becoming true here (we still think a player is
      // active, so this can't be from our own pause()) means the engine decided
      // on its own that it's done - EXCEPT loading a song into the sequencer
      // (a fresh play() that has to (re)load, e.g. switching players; also any
      // live edit while playing - transpose/BPM/instrument - which reloads the
      // same way) always briefly pauses the engine while it queues up the new
      // song with autoPlay=false, before our own seek/play messages unpause it
      // again. That pause/unpause is two separate postMessage round-trips, so
      // seq.paused can flicker true for a poll or two in between - only trust
      // it once we're genuinely near the end, not just anywhere past halfway.
      if (ratio >= 1 || (seq.paused && ratio > 0.9)) {
        // Loop: hoppa tillbaka till (den epsilon-justerade) början istället för
        // att stoppa - seekSequencerTo återanvänds medvetet här eftersom den
        // redan har fixet för att inte tappa program change/tempo-event som
        // sitter på tick 0 (samma problem som en naken seq.currentTime=0 har).
        // Ingen ny inräkning görs på loop-varv, bara vid den allra första starten.
        if (playerState?.loopEnabled) {
          seekSequencerTo(seq, 0);
          updatePlayerState(id, {
            currentTime: 0,
            progress: 0,
            timeLabel: `00:00/${total.time}`,
          });
          return;
        }

        seq.stop();
        stopProgressLoop();
        setActivePlayerId(null);
        activePlayerIdRef.current = null;
        updatePlayerState(id, {
          currentTime: 0,
          progress: 0,
          timeLabel: `00:00/${total.time}`,
        });
      }
    }, 100);
  };

  useEffect(() => {
    activePlayerIdRef.current = activePlayerId;
  }, [activePlayerId]);

  // ------------------------------------------------------
  // Inräkning (metronomklick) - genereras av appen, oberoende av midi-filens
  // eget innehåll, i takt med låtens (eventuellt override:ade) BPM och
  // spelarens uppspelningshastighet.
  // ------------------------------------------------------
  const scheduleClick = (ctx, when, accent, volume) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const peak = ((accent ? 0.5 : 0.3) * Math.max(0, Math.min(100, volume))) / 100;
    osc.type = 'square';
    osc.frequency.value = accent ? 1500 : 1000;
    gain.gain.setValueAtTime(0.0001, when);
    // exponentialRampToValueAtTime kräver ett strikt positivt målvärde
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), when + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + 0.08);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(when);
    osc.stop(when + 0.1);
    return osc;
  };

  // Avbryter en pågående inräkning för en spelare (om det finns någon) -
  // stoppar de klick som redan hunnit schemaläggas men inte spelat upp än.
  const cancelCountIn = playerId => {
    const pending = countInRef.current.get(playerId);
    if (!pending) return false;

    window.clearTimeout(pending.timeoutId);
    pending.oscillators.forEach(osc => {
      try {
        osc.stop();
      } catch (e) {
        // redan stoppad
      }
    });
    countInRef.current.delete(playerId);
    updatePlayerState(playerId, { countingIn: false });
    return true;
  };

  // ------------------------------------------------------
  // Transport
  // ------------------------------------------------------
  const internalPlay = async (playerId, fromBeginning) => {
    setKeyboardPlayerId(playerId);
    await ensureSynth();

    const seq = ensureSequencer();
    if (!seq) return;

    const isLoadedForThisPlayer = lastLoadedPlayerIdRef.current === playerId;
    const mustReload = !isLoadedForThisPlayer;

    if (mustReload) {
      const loaded = loadSongIntoSequencer(playerId);
      if (!loaded) return;
    }

    let startTime = 0;
    const saved = playersRef.current[playerId];

    if (!fromBeginning && saved && typeof saved.currentTime === 'number') {
      startTime = saved.currentTime;
    }

    if (audioContextRef.current?.state === 'suspended') {
      // Måste inväntas: audioContext.currentTime står still (eller är
      // opålitlig) förrän resume() faktiskt slagit igenom. Om vi läser
      // currentTime och schemalägger klick innan det hänt (vanligast precis
      // efter en sidladdning, då kontexten skapas suspenderad) kan det första
      // klickets schemalagda starttid redan ha "passerat" audio-klockan när
      // uppspelningen väl blir levande - webbläsaren hoppar då tyst över det.
      await audioContextRef.current.resume();
    }

    // Markera spelaren som "aktiv" redan nu (inte bara efter en eventuell
    // inräkning) - annars ser Play/Pause-knappen fortfarande ut som "Play"
    // medan klicken spelar, och man kan inte avbryta inräkningen med den.
    setActivePlayerId(playerId);
    activePlayerIdRef.current = playerId;
    lastPlayerIdRef.current = playerId;

    const rate = typeof saved?.playbackRate === 'number' ? saved.playbackRate : 1;

    const beginPlayback = () => {
      // spessasynth's playbackRate SETTER always re-triggers a full stop+reseek+play
      // internally (to recompute its internal start-time offset for the new rate),
      // even when the value is unchanged. Calling it unconditionally right after
      // seekSequencerTo() means every fresh start does TWO back-to-back reseeks:
      // the seek's own, and then this one - and that second one silences whatever
      // just started sounding (stop() calls synth.stopAllChannels()) before
      // restarting playback. Short percussive hits mask that interruption, but a
      // sustained note (e.g. a bass note starting right on tick 0) audibly gets
      // cut immediately after its attack. Only touching playbackRate when it
      // actually needs to change avoids the redundant reseek for the common case
      // (rate already correct from a previous start). Uppspelningshastighet är
      // per spelare, inte globalt state - och det finns bara en delad sequencer,
      // så den måste sättas om när en annan spelare (med annan hastighet) tar
      // över, men inte annars.
      if (seq.playbackRate !== rate) {
        seq.playbackRate = rate;
      }
      seekSequencerTo(seq, startTime);
      startProgressLoop();
    };

    // Inräkning görs bara när man startar om från början - att återuppta en
    // pausad låt mitt i ska inte räknas in igen.
    const midi = midiRef.current.get(playerId);
    const shouldCountIn = startTime <= 0 && saved?.countInEnabled && midi;

    if (shouldCountIn) {
      // Om låten redan spelar - t.ex. användaren klickar "spela från början"
      // medan den är igång - måste den tystas direkt. Annars fortsätter den
      // gamla uppspelningen ljuda samtidigt som klicken, ända tills
      // inräkningen är klar och beginPlayback() äntligen hinner köra.
      if (progressIntervalRef.current !== null) {
        stopProgressLoop();
        seq.pause();
      }

      // Om en tidigare inräkning för samma spelare redan pågår - t.ex. flera
      // klick på "spela från början" i rad innan den hunnit klart - avbryts
      // den först. Annars hörs två överlappande inräkningar samtidigt, och
      // två beginPlayback()-anrop hade konkurrerat om samma sequencer.
      cancelCountIn(playerId);

      const bpm = getMidiBpm(midi) * rate;
      // Antal klick och klicklängd beror på låtens taktart, inte alltid 4/4
      // (t.ex. 3 klick för 3/4, eller 2 klick à en punkterad fjärdedel för
      // 6/8 om filen anger det via "MIDI clocks per metronome click").
      const { beats, beatSeconds } = getCountInPlan(midi, bpm);
      const ctx = audioContextRef.current;
      // Scheduling the very first click at exactly ctx.currentTime is unreliable -
      // if the scheduling message reaches the audio thread even a hair after that
      // instant, the browser can silently skip it since its start time already
      // "passed". A small fixed lead-in guarantees the audio thread sees it ahead
      // of time. Applied uniformly to all clicks so their spacing is unaffected.
      const COUNT_IN_LEAD_IN = 0.05;
      const now = ctx.currentTime + COUNT_IN_LEAD_IN;
      const oscillators = Array.from({ length: beats }, (_, beat) => scheduleClick(ctx, now + beat * beatSeconds, beat === 0, mixer.countInVolume));

      updatePlayerState(playerId, { countingIn: true });
      const timeoutId = window.setTimeout(() => {
        countInRef.current.delete(playerId);
        updatePlayerState(playerId, { countingIn: false });
        beginPlayback();
      }, (beatSeconds * beats + COUNT_IN_LEAD_IN) * 1000);
      countInRef.current.set(playerId, { timeoutId, oscillators });
    } else {
      beginPlayback();
    }
  };


  const play = playerId => {
    internalPlay(playerId, false);
  };

  const startFromBeginning = playerId => {
    internalPlay(playerId, true);
  };

  // Uppspelningshastighet - gäller bara den aktuella spelaren (lagras i dess
  // players[playerId]-state, precis som transpose), inte globalt som mixern.
  const setPlaybackRate = (playerId, rate) => {
    const clamped = Math.min(1.5, Math.max(0.5, rate));
    updatePlayerState(playerId, { playbackRate: clamped });

    if (activePlayerIdRef.current !== playerId) return;

    const seq = sequencerRef.current;
    if (!seq) return;

    seq.playbackRate = clamped;
  };

  // Loop och inräkning - per spelare, precis som playbackRate/transpose/
  // bpmOverride, inte globalt state. Själva loopandet hanteras i
  // startProgressLoop (manuell seek till 0 istället för spessasynths inbyggda
  // loop, som har samma tick-0-bugg som seekSequencerTo fixar för vanliga
  // seekar - att lita på biblotekets egen loop skulle tappa program change/
  // tempo på varje varv).
  const setLoopEnabled = (playerId, enabled) => {
    updatePlayerState(playerId, { loopEnabled: !!enabled });
  };

  const setCountInEnabled = (playerId, enabled) => {
    updatePlayerState(playerId, { countInEnabled: !!enabled });
  };

  const setCursorEnabled = (playerId, enabled) => {
    updatePlayerState(playerId, { cursorEnabled: !!enabled });
  };

  const pause = playerId => {
    // Det finns bara en global sequencer delad mellan alla spelare, så att
    // pausa en playerId som inte är den aktiva skulle annars pausa FEL låt
    // och skriva över en annan spelares position med denna sequencerns tid.
    if (activePlayerIdRef.current !== playerId) return;

    // Om spelaren fortfarande räknar in (klicken spelar men själva låten har
    // inte startat sequencern än) finns det inget att pausa där - avbryt
    // istället inräkningen.
    if (cancelCountIn(playerId)) {
      setActivePlayerId(null);
      activePlayerIdRef.current = null;
      return;
    }

    const seq = sequencerRef.current;
    if (!seq) return;

    seq.pause();

    const duration = seq.duration || 0;
    const currentTime = seq.currentTime || 0;
    const ratio = duration > 0 ? currentTime / duration : 0;
    const t = formatTime(currentTime);
    const total = formatTime(duration);
    const timeLabel = `${t.time}/${total.time}`;

    updatePlayerState(playerId, {
      currentTime,
      progress: ratio,
      timeLabel,
    });

    stopProgressLoop();
    setActivePlayerId(null);
    activePlayerIdRef.current = null;
  };

  const seekByProgress = (playerId, progress0to1) => {
    setKeyboardPlayerId(playerId);

    const seq = sequencerRef.current;
    const playerState = playersRef.current[playerId];
    if (!playerState) return;

    const duration = playerState.duration || 0;
    const time = duration * progress0to1;

    const t = formatTime(time);
    const total = formatTime(duration);
    const timeLabel = `${t.time}/${total.time}`;

    updatePlayerState(playerId, {
      currentTime: time,
      progress: progress0to1,
      timeLabel,
    });

    if (activePlayerIdRef.current !== playerId) {
      return;
    }

    if (seq) {
      seekSequencerTo(seq, time);
    }
  };

  // ------------------------------------------------------
  // Mixer
  // ------------------------------------------------------
  const applyMainVolume = v => {
    const synth = synthRef.current;
    if (!synth) return;
    synth.setMainVolume(v / 100);
  };

  const applyMainPan = v => {
    const synth = synthRef.current;
    if (!synth) return;
    synth.setMasterPan(v / 100);
  };

  const applyChannelVolume = (channel, v) => {
    const synth = synthRef.current;
    if (!synth) return;

    synth.lockController(channel, 7, false);
    synth.controllerChange(channel, 7, v);
    synth.lockController(channel, 7, true);
  };

  const setMainVolume = v => {
    const value = Math.round(v);
    setMixer(prev => ({ ...prev, mainVolume: value }));
    applyMainVolume(value);
  };

  // Volymen på inräkningsklicken - global för alla spelare, precis som
  // övriga mixer-inställningar (till skillnad från loopEnabled/countInEnabled
  // som är per spelare). Läses direkt ur mixer när klicken schemaläggs, det
  // finns ingen "levande" ljudkälla att uppdatera i realtid mellan klicken.
  const setCountInVolume = v => {
    const value = Math.round(v);
    setMixer(prev => ({ ...prev, countInVolume: value }));
  };

  const setMainPan = v => {
    setMixer(prev => ({ ...prev, mainPan: v }));
    applyMainPan(v);
  };

  const setChannelVolume = (channel, v) => {
    const value = Math.round(v);
    setMixer(prev => {
      let key = null;
      if (channel === 0) key = 'pianoVolume';
      else if (channel === 1) key = 'bassVolume';
      else if (channel === 9) key = 'drumsVolume';
      else if (channel === 2) key = 'melodyVolume';

      if (!key) return prev;
      return { ...prev, [key]: value };
    });
    applyChannelVolume(channel, value);
  };

  // ------------------------------------------------------
  // MIDI-editor integration
  // ------------------------------------------------------
  const getParsedMidi = playerId => {
    return midiRef.current.get(playerId) ?? null;
  };

  const updateParsedMidi = (playerId, midi) => {
    if (!synthRef.current) return;

    const old = midiRef.current.get(playerId);
    // Fyll bara i från den gamla midi:n om den nya faktiskt saknar trackNames -
    // annars skriver detta över namn som just satts (t.ex. via editorns
    // "Set"-knapp för spårnamn) med de gamla, inaktuella namnen.
    if ((!midi.trackNames || !midi.trackNames.length) && old && old.trackNames && old.trackNames.length) {
      midi.trackNames = [...old.trackNames];
    }

    midiRef.current.set(playerId, midi);
    // Se till att sequencern laddas om nästa gång spelaren startas
    lastLoadedPlayerIdRef.current = null;

    const duration = midi.duration || 0;
    const total = formatTime(duration);

    const prevState = playersRef.current[playerId];
    let prevTime = prevState?.currentTime || 0;

    // Om tempot ändrats (t.ex. via BPM-redigeraren) pekar samma antal sekunder
    // nu på en annan ton i låten. Skala om positionen så den fortfarande motsvarar
    // samma musikaliska plats (samma tick) som innan ändringen.
    if (old && prevTime > 0) {
      const oldBpm = getMidiBpm(old);
      const newBpm = getMidiBpm(midi);
      if (oldBpm > 0 && newBpm > 0 && oldBpm !== newBpm) {
        prevTime = Math.min(duration, prevTime * (oldBpm / newBpm));
      }
    }

    updatePlayerState(playerId, {
      duration,
      currentTime: prevTime,
      progress: duration > 0 ? prevTime / duration : 0,
      timeLabel: `${formatTime(prevTime).time}/${total.time}`,
      hasSeq: true,
    });

    if (activePlayerIdRef.current !== playerId) {
      return;
    }

    const seq = ensureSequencer();
    if (!seq) return;

    const outputBuffer = midi.writeMIDI();
    seq.loadNewSongList([{ binary: outputBuffer }], false);
    seekSequencerTo(seq, prevTime);
    startProgressLoop();
  };

  // Mellanslag togglar play/pause för vilken spelare som senast fick
  // "tangentbordsfokus" (play-knappen, "spela från början" eller
  // progress-controllern - se keyboardPlayerId/internalPlay/seekByProgress).
  // Hoppar över det om användaren skriver i ett fält, så mellanslaget då
  // beter sig som vanligt (skriver ett mellanslag) istället för att styra
  // uppspelning.
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.code !== 'Space' && e.key !== ' ') return;

      const target = e.target;
      const tag = target?.tagName;
      const isTypingTarget =
        (tag === 'INPUT' && target.type !== 'range') ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        target?.isContentEditable;
      if (isTypingTarget) return;

      const id = keyboardPlayerIdRef.current;
      if (!id) return;

      e.preventDefault();

      if (activePlayerIdRef.current === id) {
        pause(id);
      } else {
        play(id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    statusMsg,
    activePlayerId,
    players,
    mixer,
    ensureSynth,
    loadMidiFromArrayBuffer,
    loadMidiFromUrl,
    play,
    startFromBeginning,
    pause,
    saveParsedMidi,
    seekByProgress,
    setMainVolume,
    setMainPan,
    setChannelVolume,
    getParsedMidi,
    updateParsedMidi,
    setTranspose,
    setPlaybackRate,
    setBpmOverride,
    setLoopEnabled,
    setCountInEnabled,
    setCursorEnabled,
    setCountInVolume,
  };

  useEffect(() => {
    return () => {
      stopProgressLoop();
      pause(activePlayerIdRef.current);
    };
  }, []);

  return (
    <MusicEngineContext.Provider value={value}>
      {children}
    </MusicEngineContext.Provider>
  );
};
